// "Warm Up" gate — guided onboarding curriculum.
//
// A one-time, hard-gated guided sequence the player must complete before
// reaching the daily puzzle. A short ordered SCREENS curriculum: push (F into
// L) → cascade → cascade-replay → JOY (the grey + orange colour beats, the undo
// lesson, then the first full self-solve, ending on the "one letter left"
// card). JOY is the LAST screen and the one that COMPLETES the run: tapping
// through its final "Q stayed…" trailing card runs off the end of the
// curriculum and fires completion. Modelled on the iOS app's guided practice;
// the iOS text pages are ignored, the instruction copy is adapted (DRAFT —
// curator adjusts any time).
//
// ISOLATION CONTRACT (the curator's #1 requirement): this module + warmup.css +
// warmup.test.ts own everything warm-up. The only edits to existing code are a
// handful of gate reads in main.ts, two optional MountOptions hooks on the
// controller (moveFilter, suppressUndo), the mountScreen seam in main.ts, and
// one empty caption root in index.html. Delete those files + the enumerated
// hooks and the app is bit-for-bit today's app.
//
// This module is the feature's brain: the
// hand-authored screen puzzles, the prescribed-move sequences, the screen
// sequencer (one per session — it owns the caption host for the whole run),
// and the warmUpCompletedAt localStorage read/write (key helpers live HERE, NOT
// in persistence.ts, so the versioned persistence schema is never touched).
//
// ARCHITECTURE (per the rev-4 review):
//   - Every screen is a real engine puzzle mounted through the controller via
//     the mountScreen seam. The run starts directly at the first screen's flick
//     (the push screen has no preface card → 'flicking' phase immediately).
//   - onGameEnd is a NO-OP for EVERY screen, including JOY (the last one). The
//     1-slot screens reach 'won' the instant their letter exits; the JOY screen
//     reaches a real WIN on its post-undo self-solve (finish2) but its win is
//     deliberately a controller no-op too — wiring completion into the win would
//     fire onComplete (the congrats popup) and clobber the trailing "Q stayed…
//     one letter left" card that the win is supposed to teach. The sequencer
//     advances on the EXIT event (onBarChange), never on win/lose.
//   - Completion fires when the player taps PAST the last screen's final
//     trailing card: handleEngineExit shows JOY's "Q stayed…" card; its tap
//     routes through afterTrailingCards → advanceScreen, whose past-end branch
//     calls handleComplete() (→ onComplete) instead of being a no-op. No screen
//     is a "finale" any more — there is no onGameEnd-driven completion path.
//   - mountScreen (provided by main.ts) = controller.unmount() +
//     controller.mount(...) + caption render, NOTHING more. The heavy teardown
//     (closeEndgame, cache/banner reset, nav/dev hide, subtitle)
//     runs ONCE at warm-up entry. ONE sequencer owns the caption host for the
//     whole session.
//   - Prescribed moves and each screen's canonical-chain solutionGraph are
//     authored INDEPENDENTLY per screen. The JOY screen deliberately
//     diverges them so the DAG walk yields correct-alt (the prescribed orange
//     move is not an edge from the current node → off-graph → orange); the blue
//     screens keep them aligned (the prescribed move IS the chain's edge).

import type { Puzzle } from './puzzle'
import type { SolutionGraph, SolutionGraphEdge } from './solutionGraph'
import {
  type Direction,
  type EncodedCell,
  directionToInt,
  encode,
} from './state'

// ---------- linear-chain solution graph builder ----------
//
// Every warm-up puzzle has exactly ONE canonical solve, and the player is hard-
// restricted to the prescribed move by the sequencer's moveFilter. We express
// that single canonical solve as a linear-chain SolutionGraph: node 0 (root) →
// node 1 → … → node N (the lone `win` node). For slot `i`, one edge per
// canonical choice runs from node `i` to node `i+1`. This reproduces the OLD
// per-slot-independent union classifier's coloring EXACTLY for these puzzles —
// a per-slot fan-out chain is the faithful DAG form of independent per-slot
// membership. The DAG walk (solutionGraph.ts) then becomes the sole authority.
//
// Returns the RUNTIME SolutionGraph shape (edges carry an EncodedCell
// `sourceCell` + int `direction`), NOT the on-wire {source_tile, …} shape — the
// warm-up puzzles are authored in code, so there is no parse step. The fields
// match exactly what walkSolutionGraph consumes.
function chainGraph(
  perSlotChoices: readonly (readonly {
    readonly sourceCell: EncodedCell
    readonly direction: number
  }[])[],
): SolutionGraph {
  const slotCount = perSlotChoices.length
  const nodes = []
  for (let i = 0; i <= slotCount; i += 1) {
    nodes.push({ id: i, win: i === slotCount })
  }
  const edges: SolutionGraphEdge[] = []
  for (let i = 0; i < slotCount; i += 1) {
    const choices = perSlotChoices[i]
    if (choices === undefined) continue
    for (const choice of choices) {
      edges.push({
        from: i,
        sourceCell: choice.sourceCell,
        direction: choice.direction,
        to: i + 1,
      })
    }
  }
  return { root: 0, nodes, edges }
}

// ---------- localStorage flag (own key, like hintFlags) ----------
//
// Single boolean-ish key: an ISO-8601 timestamp string stamped at completion,
// or null when the player has not yet finished Warm Up. NOT a field in the
// versioned persisted-state blob — Model A's gate is this one flag, checked at
// boot and at both onPlay handlers. Cleared by "Reset all stats" (main.ts) so
// the full new-player experience replays.

export const WARM_UP_COMPLETED_KEY = 'letterflick:warmUpCompletedAt' as const

export function getWarmUpCompletedAt(): string | null {
  try {
    return localStorage.getItem(WARM_UP_COMPLETED_KEY)
  } catch {
    // Treat localStorage failure as "not yet completed" — the worst case is a
    // private-mode browser shows the Warm Up each session, never a skip.
    return null
  }
}

export function setWarmUpCompletedAt(iso: string): void {
  try {
    localStorage.setItem(WARM_UP_COMPLETED_KEY, iso)
  } catch (err) {
    console.warn('Letter Flick: failed to persist warm-up flag', err)
  }
}

export function clearWarmUpCompletedAt(): void {
  try {
    localStorage.removeItem(WARM_UP_COMPLETED_KEY)
  } catch {
    // Swallow — best-effort, same as the persistence-layer removers.
  }
}

// ---------- cell constants ----------
//
// Encoded cell = row * 7 + col. Computed via encode() so the geometry stays in
// lockstep with BOARD_SIZE rather than hardcoding the row·7+col arithmetic.

// ---------- prescribed move ----------
//
// One guided flick: the cell the flicked tile occupies AT THE MOMENT OF THE
// MOVE, the direction, and the caption shown while that move is the active
// one. NOTE: "at the moment of the move" is load-bearing — the JOY self-solve
// flicks its Q twice, and the finish2 move's source is the cell where the BLUE
// move STRANDED the Q, not its initial cell.

export interface PrescribedMove {
  readonly source: EncodedCell
  readonly direction: Direction
  readonly instruction: string
  // Optional tap card shown AFTER this move commits, BEFORE the next move's
  // instruction appears (mid-screen pause beat). Board state is untouched while
  // it's up: the filter rejects every flick during the interlude; the tap
  // simply swaps in the next instruction. NOTE: no warm-up screen currently uses
  // this — JOY's former "J is blue…" interlude was folded into finish1's
  // instruction. The mechanism stays for future authored pauses; a committed
  // move with no afterCard falls straight through to the next move's caption.
  readonly afterCard?: string
  // Optional per-move UNDO beat (the JOY screen's grey move): after this move
  // commits, the screen shows `undoInstruction` and WAITS for the player's real
  // Undo tap (not a card tap) — the same wait-for-undo mechanism the screen's
  // trailing undoLesson uses. All flicks are rejected while waiting. When the
  // undo fires, the board is restored (this move taken back) and the run
  // proceeds DIRECTLY to the next prescribed move's instruction — no trailing
  // card. Only meaningful on a screen mounted with undo enabled (the orange
  // screen, the one screen that mounts with undo on). NOTE: a move with an
  // undoBeat must NOT also carry an afterCard — the beat replaces the interlude.
  readonly undoBeat?: {
    readonly undoInstruction: string
  }
}

// ---------- the screen model ----------
//
// Each screen declares: a hand-authored Puzzle (mounted via the controller
// through the mountScreen seam), its prescribed moves (always non-empty), an
// optional preface card (tap — or the correct first flick implicitly begins),
// the flick caption, and an optional trailing explanation card (tap to advance,
// or — past the LAST screen — to complete the run).
//
// Phase lifecycle per screen: ['preface'?] → 'flicking' → ['trailing'?] → next.

interface WarmUpScreen {
  // The hand-authored puzzle; mounts via the controller through the mountScreen
  // seam. Every screen has a real puzzle.
  readonly puzzle: Puzzle
  // The screen's prescribed move(s); always at least one.
  readonly prescribed: readonly PrescribedMove[]
  // Tap cards shown IN ORDER before the flick caption (the player taps through
  // each). By default the correct first flick still begins implicitly from any
  // of them; set prefaceLocksBoard to require the tap.
  readonly prefaceCards?: readonly string[]
  // When true, the board is LOCKED while the preface cards are up: the filter
  // rejects every flick (no implicit begin) — the player must tap through.
  // Used by the GO screen's rule card.
  readonly prefaceLocksBoard?: boolean
  // The flick instruction. For multi-move screens (JOY) this is the caption for
  // move 1; later moves swap to their own `instruction`.
  readonly caption: string
  // Tap cards shown IN ORDER after all prescribed moves exit; the last tap
  // advances to the next screen (or, on the undo-lesson screen, into the undo
  // phase). The card-only "talking bubble" beats of the plan — the word-bar
  // lesson, the solving loop, the colour explanations — live here.
  readonly trailingCards?: readonly string[]
  // Undo lesson (the orange screen only): after the trailing card is dismissed,
  // the screen does NOT advance — it shows `instruction` and waits for the
  // player's first real Undo tap (the screen mounts with undo ENABLED, the
  // only one that does). When the undo fires: if `trailingCard` is present, the
  // controller's onUndo swaps it in, whose tap then hands off to the post-undo
  // solve (or advances); if `trailingCard` is ABSENT (the JOY case — the "6
  // undos" copy moved to the congrats popup), the undo goes STRAIGHT into the
  // post-undo solve, no intermediate card. No remount — the undo restores THIS
  // board.
  readonly undoLesson?: {
    readonly instruction: string
    readonly trailingCard?: string
  }
  // Post-undo-lesson guided solve (the JOY screen only): after the orange undo
  // fires (the JOY undoLesson has no trailingCard — the "6 undos" copy lives in
  // the congrats popup now), the screen does NOT advance — it RESUMES
  // prescribed-move handling on the SAME mounted board (the orange undo already
  // restored it to the start state Q(0,3) J(0,5) Y(3,1) O(3,5), bar empty). The
  // player performs `postUndoMoves` to a real WIN (the first AND ONLY full
  // self-solve in the curriculum), then taps through `postUndoTrailingCards`,
  // and the LAST of those taps COMPLETES the run (JOY is the last screen). These
  // moves reuse the same machinery as `prescribed` — moveFilter /
  // handleEngineExit / the afterCard interlude — gated by an `inPostUndo` flag
  // (see the sequencer). The win here MUST stay a controller no-op (the screen
  // omits onGameEnd): completing on the win would clobber the "Q stayed…"
  // trailing card the win is meant to teach. Completion fires only on the
  // trailing card's tap (past-end advance), never on the win.
  readonly postUndoMoves?: readonly PrescribedMove[]
  // Tap cards shown IN ORDER after the post-undo solve wins; the last tap
  // advances to the next screen — or, on the LAST screen (JOY), runs off the end
  // of the curriculum and COMPLETES the run. Empty/undefined ⇒ advance/complete
  // immediately.
  readonly postUndoTrailingCards?: readonly string[]
}

// ---------- Screen 1: push, don't fly (F into L) ----------
//
// F(3,1) flicked RIGHT strikes L(3,4); F strands at (3,3), L exits right → bar
// 'l'. Authored canonical (canonical chain = the prescribed move) → blue.
const S2_F: EncodedCell = encode(3, 1) // 22
const S2_L: EncodedCell = encode(3, 4) // 25
const WARM_UP_PUSH_PUZZLE: Puzzle = {
  id: 'warmup-push',
  targetWord: 'l',
  wordLength: 1,
  initialLayout: [S2_F, S2_L],
  letterByTokenIdx: ['f', 'l'],
  solutionGraph: chainGraph([
    [{ sourceCell: S2_F, direction: directionToInt('RIGHT') }],
  ]),
}

// ---------- Screen 4: cascade (A pushes B pushes C) ----------
//
// A(3,1) RIGHT → A strands (3,2), B strands (3,4), C(3,5) exits right → bar 'c'.
// (A sits one cell in from the left edge — curator call, off the board edge.)
const S4_A: EncodedCell = encode(3, 1) // 22
const S4_B: EncodedCell = encode(3, 3) // 24
const S4_C: EncodedCell = encode(3, 5) // 26
const WARM_UP_CASCADE_PUZZLE: Puzzle = {
  id: 'warmup-cascade',
  targetWord: 'c',
  wordLength: 1,
  initialLayout: [S4_A, S4_B, S4_C],
  letterByTokenIdx: ['a', 'b', 'c'],
  solutionGraph: chainGraph([
    [{ sourceCell: S4_A, direction: directionToInt('RIGHT') }],
  ]),
}

// ---------- Screen: cascade replay (the alternative move, hands-on) ----------
//
// SAME layout as the cascade screen — the remount IS the board reset. The
// player now performs the alternative the pause card described: B(3,3) RIGHT
// strikes C(3,5) directly; B strands at (3,4), C exits right → bar 'c', and
// the A stays at (3,1) untouched — exactly the claim being demonstrated.
const WARM_UP_CASCADE_REPLAY_PUZZLE: Puzzle = {
  id: 'warmup-cascade-replay',
  targetWord: 'c',
  wordLength: 1,
  initialLayout: [S4_A, S4_B, S4_C],
  letterByTokenIdx: ['a', 'b', 'c'],
  solutionGraph: chainGraph([
    [{ sourceCell: S4_B, direction: directionToInt('RIGHT') }],
  ]),
}

// ---------- Screen: grey → orange + the undo lesson (player PERFORMS both)
//
// Real geometry — the shipped PIE practice puzzle relabelled to spell JOY and
// raised one row (Y + O up from row 4 to row 3, clear of the caption banner;
// same shape, engine re-verified). The screen now teaches TWO incorrect-colour
// beats, each with its own real Undo (two pips spent — the daily budget is 6):
//   GREY  (move 1): O(3,5) LEFT → O walks (3,4),(3,3),(3,2), strikes Y(3,1);
//     O rests at (3,2), Y exits LEFT. exitedLetter 'y' mismatches slot-0
//     target 'j' → grey ('incorrect'). The move's undoBeat waits for the
//     player's Undo, restores the board, and proceeds straight to move 2.
//   ORANGE (move 2): O(3,5) UP — verbatim the original orange beat (below).
// The prescribed flick is O(3,5) UP:
// O walks (2,5),(1,5), strikes J(0,5); O rests at (1,5), J exits the top
// → bar 'j'. 'j' MATCHES slot 0 of 'joy', but the provenance {O@26, UP} is NOT
// slot 0's canonical {Q@3, RIGHT} → classifySlot yields correct-alt (orange):
// right letter, wrong flick — the lesson, performed by hand. The wrong flick
// also strands the board dead (see below), so the undo lesson lives HERE: the
// player's Undo rescues the puzzle, restoring the full pre-flick snapshot (J
// back to (0,5), O back to (3,5), bar empty, one undo pip spent).
//
// The canonical chain is the PIE puzzle's real canonical set shifted with the
// board (d=1 DOWN, d=3 RIGHT):
//   slot 0: Q(0,3) RIGHT   slot 1: Y(3,1) RIGHT   slot 2: (0,4) DOWN
// After the orange flick the board (Q(0,3), Y(3,1), O(1,5)) has ZERO legal
// flicks — intentionally dead. The loss checks are undo-cap-gated (warm-up
// never burns an undo) so the screen stays 'playing'; the sequencer advances
// on the exit event, then the trailing card.
const S6_Q: EncodedCell = encode(0, 3) // 3
const S6_J: EncodedCell = encode(0, 5) // 5
const S6_Y: EncodedCell = encode(3, 1) // 22
const S6_O: EncodedCell = encode(3, 5) // 26
const WARM_UP_ORANGE_PUZZLE: Puzzle = {
  id: 'warmup-orange',
  targetWord: 'joy',
  wordLength: 3,
  initialLayout: [S6_Q, S6_J, S6_Y, S6_O],
  letterByTokenIdx: ['q', 'j', 'y', 'o'],
  solutionGraph: chainGraph([
    [{ sourceCell: encode(0, 3), direction: directionToInt('RIGHT') }],
    [{ sourceCell: encode(3, 1), direction: directionToInt('RIGHT') }],
    [{ sourceCell: encode(0, 4), direction: directionToInt('DOWN') }],
  ]),
}

// ---------- the ordered SCREENS curriculum ----------
//
// The plan's numbered screens collapse into 4 WarmUpScreen entries: the "word
// bar" lesson is the push screen's trailing card; the alternative-move lesson is
// the cascade screen's trailing card, performed hands-on by the replay screen;
// the colour lessons, the undo lesson, the first full self-solve, and the "one
// letter left" close all ride on the JOY screen (whose grey + orange beats teach
// the hint colours, whose wrong orange flick strands the board dead so Undo
// rescues it, and whose post-undo solve is the player's first real win). JOY is
// the LAST screen: tapping through its "Q stayed…" card completes the run. The
// welcome splash popup (warmupWelcome.ts) plays before the run starts.

export const WARM_UP_SCREENS: readonly WarmUpScreen[] = [
  // Screen 1 (+ word-bar lesson): push, don't fly.
  {
    puzzle: WARM_UP_PUSH_PUZZLE,
    prescribed: [
      {
        source: S2_F,
        direction: 'RIGHT',
        instruction: 'Learn to Flick!\nTouch the F then move your finger or mouse towards the L and let go.',
      },
    ],
    caption: 'Learn to Flick!\nTouch the F then move your finger or mouse towards the L and let go.',
    trailingCards: [
      'You flicked the F into the L to push the L into the word bar below.\nTap here to continue.',
    ],
  },
  // Screen: cascades.
  {
    puzzle: WARM_UP_CASCADE_PUZZLE,
    prescribed: [
      {
        source: S4_A,
        direction: 'RIGHT',
        instruction:
          'Flicks can combine. Flick the A into the B to push the C into the word bar.',
      },
    ],
    caption:
      'Flicks can combine. Flick the A into the B to push the C into the word bar.',
    // Pause beat: the alternative-move insight. Its tap advances to the
    // cascade-replay screen, whose remount resets A/B/C to their starting
    // cells so the player performs the alternative themselves.
    trailingCards: [
      'If we wanted the A to stay put, we could have flicked the B into the C.\nTap here to continue.',
    ],
  },
  // Screen: cascade replay — same board, the player makes the alternative move.
  {
    puzzle: WARM_UP_CASCADE_REPLAY_PUZZLE,
    prescribed: [
      {
        source: S4_B,
        direction: 'RIGHT',
        instruction: 'Flick the B into the C and notice the board is left in a different position.',
      },
    ],
    caption: 'Flick the B into the C and notice the board is left in a different position.',
    trailingCards: [
      'Board position matters because you cannot flick a letter directly into a letter touching it, or directly into the word bar.\nTap here to continue.',
    ],
  },
  // Screen: JOY — the grey + orange colour beats, the undo lesson, and the
  // first full self-solve, ending on the "one letter left" card. This is the
  // LAST screen: tapping through its final "Q stayed…" trailing card runs off
  // the end of the curriculum and COMPLETES the run. (The blue lesson is taught
  // inline on the post-undo solve — the "J is blue…" copy opens finish1's
  // instruction; there is no standalone blue screen and no interlude card.)
  {
    puzzle: WARM_UP_ORANGE_PUZZLE,
    prescribed: [
      // Move 1 (GREY beat): O(3,5) LEFT → O walks (3,4),(3,3),(3,2), strikes
      // Y(3,1); O rests at (3,2), Y exits LEFT. exitedLetter 'y' mismatches
      // slot-0 target 'j' → classifies grey ('incorrect'). After the flick the
      // screen waits for the player's real Undo (undoBeat). The undo restores
      // the full pre-flick snapshot (Y back to (3,1), O back to (3,5), bar
      // empty), then the run proceeds DIRECTLY to move 2's instruction — no
      // trailing card. This burns the first of two undo pips on the screen.
      {
        source: S6_O,
        direction: 'LEFT',
        instruction:
          "The word bar gives hints, let's see them. Flick the O into the Y and see the incorrect move hint.",
        undoBeat: {
          undoInstruction:
            'Incorrect moves are grey.\nUse an Undo to reset that flick.\nTap Undo below.',
        },
      },
      // Move 2 (ORANGE beat, existing): O(3,5) UP → O walks (2,5),(1,5),
      // strikes J(0,5); O rests at (1,5), J exits top. 'j' matches slot 0 but
      // the provenance {O@26, UP} ≠ slot 0's canonical {Q@3, RIGHT} → orange.
      // The board is then dead; the screen-level undoLesson rescues it.
      {
        source: S6_O,
        direction: 'UP',
        instruction:
          'Now flick the O up to push the J into the word bar and see the Orange hint color.',
      },
    ],
    caption:
      "The word bar gives hints, let's see them. Flick the O into the Y and see the incorrect move hint.",
    // No trailingCards: the orange explanation IS the wait-for-undo card
    // (afterTrailingCards falls straight through to the undoing phase). The
    // wrong flick stranded the board dead; the undo restores the full
    // pre-flick snapshot (this screen mounts with undo enabled).
    undoLesson: {
      instruction:
        'Orange hint means right letter, wrong flick. Here J is the first letter of JOY, but the board is now unsolvable.\nTap Undo below.',
    },
    // Post-undo guided BLUE solve — JOY becomes the first full self-solve. The
    // orange undo already restored the board to the start state (Q(0,3),
    // J(0,5), Y(3,1), O(3,5), bar empty), so these three moves run on that same
    // mounted board to a real WIN. Each is a canonical BLUE move (its
    // provenance IS the chain solutionGraph's slot edge → correct-canonical):
    //   1. BLUE   : Q(0,3) RIGHT → strikes J(0,5); J exits right (slot 0),
    //               Q strands at (0,4). No interlude card — the "J is blue…"
    //               copy now leads finish1's instruction instead.
    //   2. FINISH1: Y(3,1) RIGHT → strikes O(3,5); O exits right (slot 1),
    //               Y strands at (3,4). Its instruction OPENS with "J is blue
    //               because that is the correct move." (the former interlude
    //               card's text, folded in — no separate tap beat).
    //   3. FINISH2: the STRANDED Q at (0,4) DOWN → strikes Y(3,4); Y exits down
    //               (slot 2) → bar spells JOY = WIN. Source is Q's strand cell
    //               from move 1, NOT its start cell (load-bearing).
    // The win is a controller no-op (this screen omits onGameEnd) — no
    // completion, no victory animation. Q stays on the board: the trailing card
    // teaches "one letter always left", and tapping THROUGH it (the last screen's
    // last card) runs off the end of the curriculum and completes the run.
    postUndoMoves: [
      {
        source: encode(0, 3),
        direction: 'RIGHT',
        instruction: 'Now you try! Flick Q into J',
      },
      {
        source: encode(3, 1),
        direction: 'RIGHT',
        instruction: 'J is blue because that is the correct move. Finish spelling JOY. Flick the Y into the O to push the O into the word bar.',
      },
      {
        source: encode(0, 4),
        direction: 'DOWN',
        instruction: 'Flick the Q down into the Y to finish JOY!',
      },
    ],
    postUndoTrailingCards: [
      'Victory always leaves one last pusher letter on the board, in this case Q.\nTap here to continue.',
    ],
  },
]

// ---------- caption rendering ----------
//
// Self-contained caption rendering: own class names, independent layout.
// Built with createElement + textContent (no innerHTML). A plain caption is a
// non-tappable <div>; a card is a real <button> (keyboard-focusable,
// Enter/Space for free — same precedent as the game-over banner).

function renderCaption(host: HTMLElement, text: string): void {
  const el = document.createElement('div')
  el.className = 'warmup-caption'
  el.textContent = text
  host.replaceChildren(el)
}

function renderCard(host: HTMLElement, text: string, onTap: () => void): void {
  const el = document.createElement('button')
  el.type = 'button'
  el.className = 'warmup-caption warmup-caption--begin'
  el.textContent = text
  el.addEventListener('click', onTap, { once: true })
  host.replaceChildren(el)
}

function clearCaption(host: HTMLElement): void {
  host.replaceChildren()
}

// ---------- the mount-screen seam ----------
//
// main.ts hands the sequencer a mountScreen closure capturing the controller +
// landmark elements. It is EXACTLY controller.unmount() + controller.mount(...)
// with the screen's hooks (persistEnabled:false, suppressUndo:true baked in by
// main.ts) — nothing else. No screen wires onGameEnd: every screen's win is a
// controller no-op (completion is driven by the past-end trailing-card tap, not
// any win — see the sequencer's advanceScreen → handleComplete).

export interface MountScreenHooks {
  // The current screen's prescribed-move filter (MountOptions.moveFilter).
  readonly moveFilter: (source: EncodedCell, direction: Direction) => boolean
  // Advances the sequencer on each successful flick commit (MountOptions
  // .onBarChange). The args match the controller's signature but are ignored —
  // the sequencer tracks its own cursor.
  readonly onBarChange: () => void
  // Wired ONLY for the undo-lesson screen (JOY): mounts with undo ENABLED
  // (main.ts maps this to suppressUndo: false). Undefined ⇒ undo suppressed.
  readonly allowUndo?: boolean
  // Wired ONLY for the undo-lesson screen: the controller's MountOptions.onUndo
  // — fires after a successful undo's renders; advances the undo lesson.
  readonly onUndo?: () => void
  // VESTIGIAL — no screen wires it now that JOY completes via advanceScreen
  // past-end; kept so the main.ts mountScreen seam stays byte-identical /
  // isolation contract intact. main.ts reads hooks.onGameEnd to decide whether
  // to forward an onGameEnd to the controller; the sequencer never sets it, so
  // that forward is always skipped. Do NOT remove without editing main.ts.
  readonly onGameEnd?: () => void
}

export interface WarmUpSequencerDeps {
  // Caption host (the empty #warmup-caption-root in .board-stage).
  readonly captionHost: HTMLElement
  // unmount + mount a screen with the given hooks.
  readonly mountScreen: (puzzle: Puzzle, hooks: MountScreenHooks) => void
  // The real completion handler (stamp flag → toast → mountDaily → recheck).
  // Invoked exactly once, when the player taps through the LAST screen's final
  // trailing card (the past-end advance), never on a win.
  readonly onComplete: () => void
}

export interface WarmUpSequencer {
  // Begin the curriculum at screen 0. Idempotent — a second call is a no-op.
  start(): void
  // Clear the caption. Called by main.ts when the warm-up gate is torn down
  // (e.g. at completion, or on skip).
  teardown(): void
  // ---- test surface (headless) ----
  // The current screen index (0-based).
  readonly screenIndex: number
  // The current screen's active engine move filter. Mirrors what the live
  // mountScreen hook receives; rejects everything during preface? no — see
  // moveFilter contract below. Pure read of the cursor.
  moveFilter(source: EncodedCell, direction: Direction): boolean
  // Simulate a successful engine flick commit (the controller's onBarChange).
  notifyExit(): void
  // Simulate a tap on the currently-shown card (preface or trailing).
  tapCard(): void
  // Simulate a completed controller undo (the controller's onUndo) — the
  // undo-lesson advance.
  notifyUndo(): void
}

// Build the single session sequencer. Mounts the first screen immediately on
// start(). Owns the caption host for the WHOLE run.
export function createWarmUpSequencer(
  deps: WarmUpSequencerDeps,
): WarmUpSequencer {
  let started = false
  let screenIndex = 0
  // 'preface' (card up, awaiting tap or implicit begin), 'flicking' (awaiting
  // the prescribed flick(s)), 'interlude' (mid-screen afterCard up between two
  // prescribed moves, awaiting tap — board state untouched), 'trailing'
  // (explanation card up, awaiting tap), 'undoing' (an undo instruction is up,
  // awaiting the player's real Undo tap — either the screen's trailing
  // undoLesson OR a per-move undoBeat; pendingMoveUndo disambiguates),
  // 'undoTrailing' (undo-lesson card up, awaiting tap), 'done' (run complete —
  // the past-end trailing tap fired onComplete; inert).
  let phase:
    | 'preface'
    | 'flicking'
    | 'interlude'
    | 'trailing'
    | 'undoing'
    | 'undoTrailing'
    | 'done' = 'preface'
  // Index of the NEXT prescribed move within the current screen.
  let step = 0
  // Index of the CURRENT card within the active preface/trailing card queue.
  let cardIdx = 0
  // When a prescribed move carries a per-move `undoBeat`, this holds it while
  // the screen sits in the move-level 'undoing' phase (instruction up, awaiting
  // the player's real Undo). The undo handler branches on this: a non-undefined
  // value means "this is a per-move undo → restore + proceed to the next move's
  // instruction (no trailing card)"; undefined means the undo is the screen's
  // trailing undoLesson. Cleared the moment that undo fires.
  let pendingMoveUndo: { readonly undoInstruction: string } | undefined =
    undefined
  // True once the JOY screen's undo lesson has finished and the player is
  // performing the post-undo BLUE solve on the SAME mounted board. While set,
  // the step cursor + trailing-card queue read from the screen's
  // `postUndoMoves` / `postUndoTrailingCards` instead of `prescribed` /
  // `trailingCards`, so moveFilter, handleEngineExit, and the interlude path
  // all drive the new guided solve with zero new sequencing code. Reset by
  // enterScreen on every screen change.
  let inPostUndo = false
  // Hard inertness latch: once teardown() runs, EVERY external entry point
  // (start, the move filter, exit/undo/tap notifications) early-returns.
  // Production safety previously relied on the destination mount unmounting the
  // controller so the hooks stop firing; this latch makes the sequencer
  // self-inert instead — closing the skip-vs-completion race (a stray exit/tap
  // event after a skip's teardown can no longer fire onComplete and pop the
  // congrats dialog over the freshly-mounted daily). handleComplete also checks
  // it, so a completion tap that loses the race to teardown is dropped.
  let tornDown = false

  function currentScreen(): WarmUpScreen | undefined {
    return WARM_UP_SCREENS[screenIndex]
  }

  // The move sequence the cursor is currently walking: the post-undo BLUE solve
  // once the orange undo lesson hands off (inPostUndo), else the screen's
  // prescribed moves. Single source of truth for moveFilter + handleEngineExit
  // so the post-undo solve reuses every existing sequencing branch unchanged.
  function activeMoves(): readonly PrescribedMove[] {
    const screen = currentScreen()
    if (screen === undefined) return []
    if (inPostUndo) return screen.postUndoMoves ?? []
    return screen.prescribed
  }

  // The trailing-card queue for the active sequence: the post-undo cards while
  // inPostUndo, else the screen's normal trailing cards.
  function activeTrailingCards(): readonly string[] {
    const screen = currentScreen()
    if (screen === undefined) return []
    if (inPostUndo) return screen.postUndoTrailingCards ?? []
    return screen.trailingCards ?? []
  }

  // Enter screen `i`: mount its board, set the phase, render its first caption
  // (preface card or flick caption).
  function enterScreen(i: number): void {
    screenIndex = i
    step = 0
    pendingMoveUndo = undefined
    inPostUndo = false
    const screen = WARM_UP_SCREENS[i]
    if (screen === undefined) return

    // Mount via the seam. No screen wires onGameEnd — completion is driven by
    // the past-end trailing-card tap (advanceScreen → handleComplete), never a
    // win. Undo enabled + onUndo wired ONLY for the undo-lesson screen (JOY).
    const hooks: MountScreenHooks = {
      moveFilter,
      onBarChange: handleEngineExit,
      ...(screen.undoLesson !== undefined
        ? { allowUndo: true, onUndo: handleUndo }
        : {}),
    }
    deps.mountScreen(screen.puzzle, hooks)

    const firstPreface = screen.prefaceCards?.[0]
    if (firstPreface !== undefined) {
      phase = 'preface'
      cardIdx = 0
      renderCard(deps.captionHost, firstPreface, nextPrefaceCard)
    } else {
      phase = 'flicking'
      renderCaption(deps.captionHost, screen.caption)
    }
  }

  // Tap on the current preface card: show the next card in the queue, or —
  // queue exhausted — swap to the flick caption and move into 'flicking'.
  // (The correct first flick still implicitly begins from ANY preface card;
  // handleEngineExit jumps the phase directly.)
  function nextPrefaceCard(): void {
    if (phase !== 'preface') return
    const screen = currentScreen()
    if (screen === undefined) return
    cardIdx += 1
    const next = screen.prefaceCards?.[cardIdx]
    if (next !== undefined) {
      renderCard(deps.captionHost, next, nextPrefaceCard)
      return
    }
    phase = 'flicking'
    renderCaption(deps.captionHost, screen.caption)
  }

  // Advance to the next screen — or, PAST the last screen, complete the run.
  // The last screen (JOY) has no successor: tapping through its final "Q
  // stayed…" trailing card runs off the end of the curriculum, so this is the
  // SOLE completion path. There is no win-driven completion any more.
  function advanceScreen(): void {
    const next = screenIndex + 1
    if (next >= WARM_UP_SCREENS.length) {
      // Past the last screen: the player tapped through JOY's final trailing
      // card. Fire completion (guarded against teardown / double-fire).
      handleComplete()
      return
    }
    enterScreen(next)
  }

  // The current screen's prescribed-move filter. Accepts the current step's
  // move during 'preface' (implicit begin — unless the screen locks its board
  // during the preface) and 'flicking'; rejects everything in every other
  // phase (interlude / trailing / undoing / undoTrailing / done). Pure read
  // of the cursor — no side effects.
  function moveFilter(source: EncodedCell, direction: Direction): boolean {
    if (tornDown) return false
    if (phase !== 'preface' && phase !== 'flicking') return false
    const screen = currentScreen()
    if (screen === undefined) return false
    if (phase === 'preface' && screen.prefaceLocksBoard === true) return false
    const move = activeMoves()[step]
    if (move === undefined) return false
    return source === move.source && direction === move.direction
  }

  // The controller fired onBarChange after a successful engine flick committed.
  // Advance the step cursor + caption; when all prescribed moves are done,
  // either show the trailing card, or advance (past the last screen ⇒ complete).
  function handleEngineExit(): void {
    if (tornDown) return
    const screen = currentScreen()
    if (screen === undefined) return
    // Implicit begin: a correct first flick made before the preface card was
    // tapped IS the begin. Mark started so a late tap can't rewind.
    if (phase === 'preface') {
      started = true
      phase = 'flicking'
    }
    step += 1
    const moves = activeMoves()
    const justCommitted = moves[step - 1]
    const nextMove = moves[step]
    if (nextMove !== undefined) {
      // Per-move UNDO beat: the committed move must be taken back before the
      // next move makes sense (the JOY grey beat). Show its undo instruction
      // and WAIT for the player's real Undo — the filter rejects every flick
      // during 'undoing'. handleUndo then restores the board and resumes at the
      // next move's instruction (no trailing card). Checked before afterCard;
      // the two are mutually exclusive by contract.
      if (justCommitted?.undoBeat !== undefined) {
        pendingMoveUndo = justCommitted.undoBeat
        phase = 'undoing'
        renderCaption(deps.captionHost, justCommitted.undoBeat.undoInstruction)
        return
      }
      // Mid-screen pause beat: the committed move's afterCard (if any) shows
      // first; its tap swaps in the next instruction. Board state untouched —
      // the filter rejects everything during 'interlude'.
      if (justCommitted?.afterCard !== undefined) {
        phase = 'interlude'
        renderCard(deps.captionHost, justCommitted.afterCard, resumeAfterInterlude)
        return
      }
      renderCaption(deps.captionHost, nextMove.instruction)
      return
    }
    // All prescribed moves exited. Show the trailing card(s); the past-end tap
    // through the LAST screen's last card is what completes the run.
    const firstTrailing = activeTrailingCards()[0]
    if (firstTrailing !== undefined) {
      phase = 'trailing'
      cardIdx = 0
      renderCard(deps.captionHost, firstTrailing, nextTrailingCard)
      return
    }
    afterTrailingCards()
  }

  // Tap on the current trailing card: show the next card in the queue, or —
  // queue exhausted — route via afterTrailingCards.
  function nextTrailingCard(): void {
    if (phase !== 'trailing') return
    const screen = currentScreen()
    if (screen === undefined) return
    cardIdx += 1
    const next = activeTrailingCards()[cardIdx]
    if (next !== undefined) {
      renderCard(deps.captionHost, next, nextTrailingCard)
      return
    }
    afterTrailingCards()
  }

  // Tap on a mid-screen interlude card: swap back to 'flicking' with the next
  // prescribed move's instruction.
  function resumeAfterInterlude(): void {
    if (phase !== 'interlude') return
    const screen = currentScreen()
    const nextMove = activeMoves()[step]
    if (screen === undefined || nextMove === undefined) return
    phase = 'flicking'
    renderCaption(deps.captionHost, nextMove.instruction)
  }

  // The trailing card queue is exhausted: screens with an undo lesson enter
  // the 'undoing' phase (instruction caption up, awaiting the player's real
  // Undo tap on the game's undo button); everything else advances.
  function afterTrailingCards(): void {
    const screen = currentScreen()
    // The undo lesson only fires the FIRST time through (the orange trailing
    // beat). Once we are in the post-undo solve, its trailing cards advance
    // straight to the next screen — re-entering the undo lesson would loop.
    if (!inPostUndo && screen?.undoLesson !== undefined) {
      phase = 'undoing'
      renderCaption(deps.captionHost, screen.undoLesson.instruction)
      return
    }
    advanceScreen()
  }

  // The controller completed a successful undo (MountOptions.onUndo — wired
  // only for screens with undo enabled). Two distinct undos can fire on the
  // JOY screen:
  //
  //   1. Per-move undo (pendingMoveUndo set): the grey move's undoBeat. The
  //      board has been restored; proceed DIRECTLY to the next move's
  //      instruction — NO trailing card. Only valid in the move-level 'undoing'
  //      phase (we enter it straight after the grey exit, so there is no eager
  //      window to guard).
  //
  //   2. Screen-level undo (undoLesson): the final orange beat. Normal path
  //      fires during 'undoing'; the early path covers the eager player who taps
  //      Undo while the orange trailing card is still up (the flick's snapshot
  //      is already on the stack) — treated as skipping ahead. Ignored in any
  //      other phase. JOY's undoLesson has NO trailingCard ("6 undos" copy moved
  //      to the congrats popup), so its undo hands off DIRECTLY into the
  //      post-undo BLUE solve (startPostUndoOrAdvance) — no intermediate card. A
  //      screen that DID declare a trailingCard would show it first
  //      ('undoTrailing'), whose tap then runs the same hand-off.
  function handleUndo(): void {
    if (tornDown) return
    if (pendingMoveUndo !== undefined) {
      if (phase !== 'undoing') return
      pendingMoveUndo = undefined
      const nextMove = activeMoves()[step]
      if (nextMove === undefined) return
      phase = 'flicking'
      renderCaption(deps.captionHost, nextMove.instruction)
      return
    }
    const screen = currentScreen()
    if (screen?.undoLesson === undefined) return
    if (phase !== 'undoing' && phase !== 'trailing') return
    // With a trailingCard: show it; its tap hands off via afterUndoLesson.
    // Without one (the JOY case — the "6 undos" copy moved to the congrats
    // popup): go STRAIGHT into the post-undo solve, no intermediate card.
    if (screen.undoLesson.trailingCard !== undefined) {
      phase = 'undoTrailing'
      renderCard(deps.captionHost, screen.undoLesson.trailingCard, afterUndoLesson)
      return
    }
    startPostUndoOrAdvance()
  }

  // The undo-lesson trailing card was tapped (only reachable when the screen
  // declares one). Hand off to the post-undo solve / advance — the same body
  // the no-trailingCard undo path runs directly.
  function afterUndoLesson(): void {
    if (tornDown) return
    if (phase !== 'undoTrailing') return
    startPostUndoOrAdvance()
  }

  // After the orange undo lesson is dismissed (via the trailingCard tap, or
  // directly on the undo when there is no card): if the screen carries a
  // post-undo guided solve (the orange/JOY screen), RE-MOUNT the screen with
  // undo SUPPRESSED, then flip into the post-undo sequence. Otherwise advance.
  //
  // Why re-mount (not resume the same board): the undo lessons mount with undo
  // ENABLED so the player can perform the grey + orange undos. But the post-undo
  // BLUE self-solve must run with undo DISABLED — otherwise the player can tap
  // Undo mid-solve, the controller reverts the engine board, and the sequencer's
  // caption/cursor desyncs into an unrecoverable state (curator-reproduced bug).
  // The orange undo already restored the board to the JOY initial layout, and a
  // remount re-inits to that SAME layout — so the swap is visually seamless —
  // but the new mount carries NO allowUndo / NO onUndo, baking suppressUndo:true
  // into the controller (committed moves stay off the undo stack, onUndoClick
  // early-returns, the 'u' key is inert). Undo is now locked for the blue solve.
  //
  // Re-entrancy: this runs from handleUndo, i.e. INSIDE the controller's live
  // onUndoClick render stack (onUndo fires as its last statement). Synchronously
  // calling mountScreen → controller.unmount()/mount() there would re-enter the
  // controller while it is still unwinding. The completion path has the exact
  // same hazard and DEFERS via microtask (main.ts onComplete). So we DEFER the
  // remount with queueMicrotask, guarded by the tornDown latch in case a
  // skip/teardown races the microtask.
  function startPostUndoOrAdvance(): void {
    const screen = currentScreen()
    const firstPostUndo = screen?.postUndoMoves?.[0]
    if (screen === undefined || firstPostUndo === undefined) {
      advanceScreen()
      return
    }
    const postUndoPuzzle = screen.puzzle
    queueMicrotask((): void => {
      if (tornDown) return
      // Re-mount the JOY board with undo SUPPRESSED: no allowUndo, no onUndo.
      // moveFilter + onBarChange stay wired so the blue solve still sequences.
      deps.mountScreen(postUndoPuzzle, {
        moveFilter,
        onBarChange: handleEngineExit,
      })
      inPostUndo = true
      step = 0
      cardIdx = 0
      phase = 'flicking'
      renderCaption(deps.captionHost, firstPostUndo.instruction)
    })
  }

  // Tap on the currently-shown card. During 'preface'/'trailing' it advances
  // through the card queue; during 'interlude' it resumes the next move;
  // during 'undoTrailing' it advances the screen. Inert otherwise.
  function tapCard(): void {
    if (tornDown) return
    if (phase === 'preface') {
      nextPrefaceCard()
    } else if (phase === 'interlude') {
      resumeAfterInterlude()
    } else if (phase === 'trailing') {
      nextTrailingCard()
    } else if (phase === 'undoTrailing') {
      afterUndoLesson()
    }
  }

  // The SOLE completion driver: the player tapped PAST the last screen's final
  // trailing card (advanceScreen's past-end branch). Guarded so a stray
  // event after teardown, or a double-fire, can't pop the congrats dialog
  // twice. Fires deps.onComplete() exactly once.
  function handleComplete(): void {
    if (tornDown) return
    if (phase === 'done') return
    phase = 'done'
    deps.onComplete()
  }

  function start(): void {
    if (tornDown) return
    if (started) return
    started = true
    enterScreen(0)
  }

  // Terminal: latches the sequencer inert (see tornDown above) and clears the
  // caption. Idempotent; there is deliberately no way back — a new run means a
  // new sequencer.
  function teardown(): void {
    tornDown = true
    clearCaption(deps.captionHost)
  }

  return {
    start,
    teardown,
    get screenIndex(): number {
      return screenIndex
    },
    moveFilter,
    notifyExit: handleEngineExit,
    tapCard,
    notifyUndo: handleUndo,
  }
}
